import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import GlobalFeedback from "../../../global/GlobalFeedback";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import { VisitorProvider } from "../components/context/VisitorContext";
import { useVisitorRole } from "../utils/useVisitorRole";
import { useVisitorInlineEdit } from "../utils/useVisitorInlineEdit";
import VisitorEditPanel from "../components/form/VisitorEditPanel";
import { useVisitorDashboardStats } from "../utils/useVisitorDashboardStats";
import { notifyVisitorDataChanged } from "../utils/visitorDataEvents";
import { handlePrintVisitorSlipStub } from "../utils/visitorSlip";
import { VISITOR_LIST_SUBMODULE_SLUG } from "../utils/visitorPermissionSlugs";
import VisitorLiveStatsPanel from "../components/metrics/VisitorLiveStatsPanel";
import VisitorDirectorySection from "../components/directory/VisitorDirectorySection";
import ExitVisitorModal from "../components/modals/ExitVisitorModal";
import DeleteVisitorModal from "../components/modals/DeleteVisitorModal";
import "../styles/visitorDashboard.css";
import "../styles/visitorTable.css";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";

const LIST_QUICK_JUMP = [
  { key: "stats", label: "Summary", targetId: "visitor-list-stats" },
  { key: "list", label: "Visitor directory", targetId: "visitor-list-directory" },
];

function VisitorListContent() {
  const location = useLocation();
  const legacyEditHandled = useRef(false);
  const perms = useVisitorRole(VISITOR_LIST_SUBMODULE_SLUG);
  const isReceptionist = perms.isReceptionist;
  const limitedEdit = perms.canLimitedEdit && !perms.canFullEdit;
  const {
    editingVisitor,
    editError,
    openEdit,
    openEditById,
    cancelEdit,
    clearEdit,
  } = useVisitorInlineEdit("visitor-list-edit");

  const showSummary = perms.canView;

  const [successMsg, setSuccessMsg] = useState("");
  const [warningMsg, setWarningMsg] = useState("");
  const [exitTarget, setExitTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const { dashboardStats, loadingStats, refreshStats } = useVisitorDashboardStats({
    enabled: showSummary,
    reloadToken,
    listScope: "self",
  });

  useEffect(() => {
    const editId = location.state?.editVisitorId;
    if (!editId || legacyEditHandled.current) return;
    legacyEditHandled.current = true;
    openEditById(editId);
    window.history.replaceState({}, document.title, location.pathname);
  }, [location.state?.editVisitorId, location.pathname, openEditById]);

  const handleSuccess = (msg) => {
    setSuccessMsg(msg);
    clearEdit();
    setReloadToken((t) => t + 1);
    notifyVisitorDataChanged();
    refreshStats({ silent: true });
  };

  const handleEditSuccess = (msg) => {
    handleSuccess(msg);
    cancelEdit();
  };

  const quickJumpItems = LIST_QUICK_JUMP.filter((item) => {
    if (item.key === "stats") return showSummary;
    if (item.key === "list") return perms.canView;
    return true;
  });

  if (editingVisitor && perms.canEdit) {
    quickJumpItems.push({
      key: "edit",
      label: "Edit visitor",
      targetId: "visitor-list-edit",
    });
  }

  const heroEyebrow = isReceptionist ? "Front desk" : "Office administration";
  const heroTitle = "Visitors";
  const heroSub =
    "Summary and directory for your visitor entries. Permissions follow visitor-list in User builder.";

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">{heroEyebrow}</p>
          <h2 className="user-builder-hero__title">{heroTitle}</h2>
          <p className="user-builder-hero__sub">{heroSub}</p>
        </div>
      </header>

      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalSuccessFlashModal
        message={warningMsg}
        onDismiss={() => setWarningMsg("")}
        title="Warning"
        tone="warning"
      />
      <GlobalFeedback type="error" text={editError} role="alert" />
      {perms.permissionsLoading ? (
        <p className="visitor-table-loading" style={{ padding: "2rem 1.5rem" }}>
          Loading permissions…
        </p>
      ) : null}
      {!perms.permissionsLoading && !perms.canView ? (
        <GlobalFeedback
          type="warning"
          text="No view permission for visitor directory. Enable View on visitor-list in User builder, then sign in again."
          role="status"
        />
      ) : null}
      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          {!perms.permissionsLoading && showSummary ? (
            <VisitorLiveStatsPanel
              sectionId="visitor-list-stats"
              loading={loadingStats}
              dashboardStats={dashboardStats}
            />
          ) : null}

          {editingVisitor && perms.canEdit ? (
            <div
              id="visitor-list-edit"
              className="user-builder-panel"
              style={{ scrollMarginTop: "2rem" }}
            >
              <VisitorEditPanel
                visitor={editingVisitor}
                limitedEdit={limitedEdit}
                onCancel={cancelEdit}
                onSuccess={handleEditSuccess}
              />
            </div>
          ) : null}

          {!perms.permissionsLoading && perms.canView ? (
            <div
              id="visitor-list-directory"
              className="user-builder-panel"
              style={{ scrollMarginTop: "2rem" }}
            >
              <VisitorDirectorySection
                listScope="self"
                showPanelHead
                permissions={perms}
                roleSlug={perms.roleSlug}
                reloadToken={reloadToken}
                emptyBaseline="No visitor entries registered by you yet."
                onEdit={perms.canEdit ? openEdit : undefined}
                onDelete={perms.canDelete ? setDeleteTarget : undefined}
                onExit={perms.canExit ? setExitTarget : undefined}
                onSlip={perms.canSlip ? () => handlePrintVisitorSlipStub() : undefined}
              />
            </div>
          ) : null}
        </div>

        {quickJumpItems.length > 0 ? (
          <GlobalQuickJump
            asideAriaLabel="Visitors sections"
            items={quickJumpItems}
            activeKey={activeQuickJump}
            onActiveChange={setActiveQuickJump}
          />
        ) : null}
      </div>

      <ExitVisitorModal
        visitor={exitTarget}
        onClose={() => setExitTarget(null)}
        onSuccess={handleSuccess}
      />

      <DeleteVisitorModal
        visitor={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onSuccess={handleSuccess}
        onError={(msg) => setWarningMsg(msg)}
      />
    </div>
  );
}

/** `/app/visitors` — Summary + visitor directory (visitor-list submodule). */
export default function VisitorListPage() {
  return (
    <VisitorProvider>
      <VisitorListContent />
    </VisitorProvider>
  );
}
